import React from "react";
import { Grid, Box, Typography, alpha, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import { red, green, blue, grey } from "@mui/material/colors";
import {
  ManageAccounts as ManageAccountsIcon,
  Autorenew as AutorenewIcon,
  EventAvailable as EventAvailableIcon,
  Security as SecurityIcon,
  Computer as ComputerIcon,
  Router as RouterIcon,
  Dvr as DvrIcon,
  LaptopMac as LaptopMacIcon,
  WarningAmber as WarningAmberIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  AssignmentInd as AssignmentIndIcon,
  VpnKey as VpnKeyIcon,
  Shield as ShieldIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import laptopSecurity from "../../../assets/illustrations/laptop_security.svg";
import security from "../../../assets/illustrations/security.PNG";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-10px) rotate(-3deg); }
  100% { transform: translateY(0px) rotate(-5deg); }
`;

const SectionTitle = ({ title, icon: Icon, color = "primary", centered = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;
  const darkColor = theme.palette[color]?.dark || theme.palette.primary.dark;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: centered ? "center" : "flex-start",
        gap: 2,
        mb: 3,
        p: 1.5,
        width: "100%",
        borderRadius: "12px",
        background: isDarkMode
          ? "rgba(255, 255, 255, 0.08)"
          : `linear-gradient(135deg, ${alpha(mainColor, 0.3)} 0%, ${alpha(mainColor, 0.1)} 100%)`,
        border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.12)" : alpha(mainColor, 0.4)}`,
        boxShadow: isDarkMode
          ? "0 4px 12px rgba(0, 0, 0, 0.2)"
          : `0 4px 12px ${alpha(mainColor, 0.15)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "10px",
          bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.12)" : "white",
          boxShadow: `0 4px 10px ${alpha(mainColor, 0.2)}`,
        }}
      >
        <Icon sx={{ fontSize: "1.3rem", color: mainColor }} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          letterSpacing: "0.02em",
          color: isDarkMode ? "rgba(255, 255, 255, 0.95)" : darkColor,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const PropertyTile = ({ label, value, icon: Icon, highlight, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "18px",
        bgcolor: alpha(theme.palette.background.paper, 0.4),
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "0%",
          bgcolor: mainColor,
          transition: "height 0.3s ease",
          borderRadius: "0 4px 4px 0",
        },
        "&:hover": {
          bgcolor: alpha(theme.palette.background.paper, 1),
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
          borderColor: alpha(mainColor, 0.2),
          "&::before": {
            height: "100%",
          },
          "& .tile-icon": {
            color: mainColor,
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
        {Icon && (
          <Icon
            className="tile-icon"
            sx={{
              fontSize: "1rem",
              color: alpha(theme.palette.text.secondary, 0.3),
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          color: highlight ? mainColor : "text.primary",
          fontWeight: 900,
          fontSize: "0.95rem",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {value || "---"}
      </Typography>
    </Box>
  );
};

const StatusIndicator = ({ status, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = status === "Prepaid" ? blue[600] : red[600];

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.2,
        py: 0.5,
        borderRadius: "8px",
        bgcolor: alpha(mainColor, 0.1),
      }}
    >
      <Typography
        sx={{
          color: mainColor,
          fontWeight: 900,
          fontSize: "0.75rem",
          letterSpacing: "0.02em",
        }}
      >
        {status?.toUpperCase() || "UNKNOWN"}
      </Typography>
    </Box>
  );
};

export const AccountConfigurationWidget = ({ user }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "white"),
        border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `2px solid ${theme.palette.primary.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.7s forwards`,
      }}
    >
      <SectionTitle title="Account Configuration" icon={SettingsIcon} color="primary" centered />
      <Grid container spacing={0} sx={{ flexGrow: 1, position: "relative", zIndex: 1 }}>
        <Grid item xs={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Grid
              container
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Grid
                item
                xs={6}
                sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  USER MODE
                </Typography>
                <StatusIndicator status="Prepaid" />
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  ACCOUNT TYPE
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.main" }}>
                  {user.accountType}
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Grid
                item
                xs={6}
                sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  AUTO RENEWAL
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: user.isEnabledAutoRenewal ? "success.main" : "text.primary",
                  }}
                >
                  {user.isEnabledAutoRenewal ? "Enabled" : "Disabled"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  LAST RENEWAL
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {user.lastRenewalDate || "---"}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                MAC SECURITY
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Disabled
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={5}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={laptopSecurity}
            sx={{
              width: "100%",
              maxWidth: "180px",
              height: "auto",
              opacity: 0.95,
              transform: "rotate(-5deg)",
              filter: "brightness(0.92) contrast(1.05)",
              animation: `${float} 5s ease-in-out infinite alternate`,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export const NetworkSecurityWidget = ({ user }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "white"),
        border: `2px solid ${alpha(theme.palette.success.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.success.main, 0.25)}`,
          border: `2px solid ${theme.palette.success.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.8s forwards`,
      }}
    >
      <SectionTitle title="Network & Security" icon={SecurityIcon} color="info" />
      <Grid container spacing={0} sx={{ flexGrow: 1, position: "relative", zIndex: 1 }}>
        <Grid item xs={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Grid
              container
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Grid
                item
                xs={6}
                sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  LOGIN IP
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "info.main" }}>
                  {user.ipAddress || "---"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  NAS IP
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {user.nasIpAddress || "---"}
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              sx={{
                mb: 2,
                pb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Grid
                item
                xs={6}
                sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  STATIC IP
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {user.staticIpAddress || "---"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  LOGIN MAC
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {user.currentMac || "---"}
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ pr: 2, borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  SESSION START
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {user.acctStartDate || "---"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  LAST LOGOFF
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {user.acctStopDate || "---"}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: user.lastRejectionMessage ? "error.main" : "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                LAST RADIUS REJECTION MSG
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: user.lastRejectionMessage ? "error.main" : "text.primary",
                }}
              >
                {user.lastRejectionMessage || "---"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid
          item
          xs={5}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={security}
            sx={{
              width: "80%",
              maxWidth: "150px",
              height: "auto",
              opacity: 0.95,
              filter: "brightness(0.92) contrast(1.05)",
              animation: `${float} 6s ease-in-out infinite`,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const AccountInformation = ({ user }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AccountConfigurationWidget user={user} />
        </Grid>
        <Grid item xs={12} md={6}>
          <NetworkSecurityWidget user={user} />
        </Grid>
      </Grid>
    </Box>
  );
};
export default AccountInformation;
